import { useEffect, useRef, useState } from 'react';
import { PhotoSlider } from 'react-photo-view';
import { X, Pencil, NotebookPen } from 'lucide-react';
import { PendingAttachment } from '@/types';
import { useModalStack } from '@/context/ModalStackContext';
import { isImageMedia, isVideoMedia } from '@/lib/media/mediaTypes';
import 'react-photo-view/dist/react-photo-view.css';

interface AttachmentPreviewModalProps {
  attachment: PendingAttachment;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onNotesUpdated?: (notes: string | null) => void; // parent maps to the previewed pending attachment
}

export default function AttachmentPreviewModal({ attachment, open, onClose, onEdit, onNotesUpdated }: AttachmentPreviewModalProps) {
  const modalId = useRef(`attachment-preview-${Date.now()}`).current;
  const { registerModal } = useModalStack();

  // Pass only `type` (not the File) so detection stays equivalent to the old
  // `file.type.startsWith('image/'|'video/')` and does not pick up filename-extension hints.
  const isImage = isImageMedia({ type: attachment.file.type });
  const isVideo = isVideoMedia({ type: attachment.file.type });

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  // Intrinsic video dimensions — react-photo-view needs these to size and center the
  // layout box. Without them the box is 0×0 and the video overflows the bottom-right.
  const [videoDims, setVideoDims] = useState<{ w: number; h: number } | null>(null);

  // Notes editor. Seeded from the attachment; the call site passes a `key` per attachment so
  // this component remounts when switching attachments, keeping the seed in sync.
  const [notesOpen, setNotesOpen] = useState(false);
  const [draftNotes, setDraftNotes] = useState(attachment.notes ?? '');

  const handleNotesSave = () => {
    const trimmed = draftNotes.trim();
    onNotesUpdated?.(trimmed === '' ? null : trimmed);
    setNotesOpen(false);
  };

  const handleNotesCancel = () => {
    setDraftNotes(attachment.notes ?? '');
    setNotesOpen(false);
  };

  // Register in the modal stack so Android hardware-back closes this first.
  useEffect(() => {
    if (!open) return;
    return registerModal({ id: modalId, priority: 9999, onClose });
  }, [open, registerModal, modalId, onClose]);

  // Build (and clean up) an object URL so videos can play; images use their data-URL preview directly.
  // Also probe the video's natural dimensions so PhotoSlider can fit/center it correctly.
  useEffect(() => {
    if (!open || !isVideo) {
      setVideoUrl(null);
      setVideoDims(null);
      return;
    }
    const url = URL.createObjectURL(attachment.file);
    setVideoUrl(url);

    // Throwaway element just to read videoWidth/videoHeight from metadata.
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () =>
      setVideoDims({
        w: probe.videoWidth || window.innerWidth,
        h: probe.videoHeight || window.innerHeight,
      });
    probe.onerror = () =>
      setVideoDims({ w: window.innerWidth, h: window.innerHeight });
    probe.src = url;

    return () => {
      probe.onloadedmetadata = null;
      probe.onerror = null;
      probe.removeAttribute('src');
      URL.revokeObjectURL(url);
    };
  }, [open, isVideo, attachment.file]);

  if (!open || (!isImage && !isVideo)) return null;

  // For videos, wait until we have both the playable URL and the intrinsic dimensions so the
  // PhotoSlider builds a correctly-sized, centered box (reading metadata from a local object
  // URL is effectively instant, so this does not cause a visible flash).
  if (isVideo && (!videoUrl || !videoDims)) return null;

  const images = [
    isVideo
      ? {
          key: 'attachment-video',
          // Intrinsic size lets react-photo-view fit + center the box; the video then fills it.
          width: videoDims!.w,
          height: videoDims!.h,
          render: ({ attrs }: { attrs: Partial<React.HTMLAttributes<HTMLElement>> }) => (
            <video
              {...attrs}
              src={videoUrl!}
              controls
              playsInline
              autoPlay
              // Keep the library-computed width/height; object-fit guards aspect ratio.
              style={{ ...(attrs?.style as React.CSSProperties), objectFit: 'contain' }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        }
      : { key: 'attachment-image', src: attachment.preview || undefined },
  ];

  return (
    <PhotoSlider
      images={images}
      visible={open}
      onClose={onClose}
      index={0}
      onIndexChange={() => {}}
      maskOpacity={0.9}
      overlayRender={() => (
        <div className="fixed inset-0 pointer-events-none z-[1000000000]">
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent pointer-events-auto">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors text-white"
              aria-label="סגור"
            >
              <X className="w-6 h-6" />
            </button>

            {isImage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
              >
                <Pencil className="w-5 h-5" />
                <span className="text-sm">ערוך</span>
              </button>
            )}
          </div>

          {/* Bottom bar: notes button / preview, or the notes editor when open. */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent pointer-events-auto">
            {notesOpen ? (
              <div
                className="flex flex-col gap-2"
                dir="rtl"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <textarea
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  rows={3}
                  dir="rtl"
                  placeholder="הוסף הערות לקובץ..."
                  className="w-full resize-none rounded-lg bg-black/60 text-white placeholder-white/50 p-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNotesCancel();
                    }}
                    className="px-4 py-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors text-white text-sm font-medium"
                  >
                    ביטול
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNotesSave();
                    }}
                    className="px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity text-sm"
                  >
                    שמור
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3" dir="rtl">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDraftNotes(attachment.notes ?? '');
                    setNotesOpen(true);
                  }}
                  className={`shrink-0 p-2 rounded-full transition-colors ${
                    attachment.notes
                      ? 'bg-primary text-primary-foreground hover:opacity-90'
                      : 'bg-black/50 hover:bg-black/70 text-white'
                  }`}
                  aria-label="עריכת הערות"
                  title="עריכת הערות"
                >
                  <NotebookPen className="w-6 h-6" />
                </button>
                {attachment.notes && (
                  <p className="text-white/70 text-sm line-clamp-2" dir="rtl">
                    {attachment.notes}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
}
